// src/components/PlayerProfile/PlayerEntriesModal.jsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { X, Clock, User, Loader2, GamepadIcon,ChevronRight, ChevronUp, ChevronDown, Filter, MessageSquare, Send, Edit2, Check } from 'lucide-react';
import { playerProfileService } from '../../apis/playerProfileService';
import toast from 'react-hot-toast';
import { isSingleSmsSkipped } from '../../utils/smsSendResult';

// Default SMS Template
const DEFAULT_SMS_TEMPLATE = `EPL: Cards, cocktails, food, and a great crowd — starting today at 6 PM.
Join us for our 10k Monthly Classic tonight!

View details: https://www.elitepokerlounge.com/assets/uploads/events_tournaments/695bb883dfea3.webp

Msg & data rates may apply.
Reply STOP to opt out, HELP for help.
Terms: https://elitepokerlounge.com/terms | Privacy: https://elitepokerlounge.com/privacy`;

// Hourly Time Slots Modal Component
const HourlyTimeSlotsModal = ({ isOpen, onClose, quickTimeRanges, onApplyTimeRange }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[80vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Select Hourly Time Slot</h3>
            <p className="text-sm text-gray-500">Choose a time range to filter entries</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {quickTimeRanges.slice(8, -3).map((range, index) => (
              <button
                key={index + 8}
                onClick={() => {
                  onApplyTimeRange(range.start, range.end);
                  onClose();
                }}
                className="px-3 py-2 text-sm bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors text-left border border-green-200 hover:border-green-300"
              >
                {range.label}
              </button>
            ))}
          </div>
        </div>

        <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-3 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

const PlayerEntriesModal = ({ player, isOpen, onClose }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isHourlyModalOpen, setIsHourlyModalOpen] = useState(false);
  const [showMessaging, setShowMessaging] = useState(false);
  const [messageText, setMessageText] = useState(DEFAULT_SMS_TEMPLATE);
  const [isMessageEditable, setIsMessageEditable] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);

  // Filter states
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    gameType: '',
    sortBy: 'open_date',
    sortOrder: 'desc',
    timeRangeStart: '',
    timeRangeEnd: ''
  });

  // Generate time options in 30-minute increments
  const timeOptions = useMemo(() => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const displayTime = new Date(`2000-01-01T${timeString}`).toLocaleTimeString('en-US', {
          hour: 'numeric',
          minute: '2-digit',
          hour12: true
        });
        options.push({ value: timeString, display: displayTime });
      }
    }
    return options;
  }, []);

  const fetchPlayerEntries = useCallback(async () => {
    setLoading(true);
    try {
      const response = await playerProfileService.getPlayerEntries(player.player_number);
      if (response && response.success) {
        setEntries(response.data);
      }
    } catch (error) {
      console.error('Error fetching player entries:', error);
    } finally {
      setLoading(false);
    }
  }, [player?.player_number]);

  useEffect(() => {
    if (isOpen && player) {
      fetchPlayerEntries();
    }
  }, [isOpen, player, fetchPlayerEntries]);

  // Get unique game types
  const gameTypes = useMemo(() => {
    const types = [...new Set(entries.map(entry => entry.game_name).filter(Boolean))];
    return types.sort();
  }, [entries]);

  // Helper function to check if a time falls within a range
  const isTimeInRange = (dateTime, startTime, endTime) => {
    if (!startTime || !endTime || !dateTime) return true;

    const entryTime = new Date(dateTime);
    const entryHours = entryTime.getHours();
    const entryMinutes = entryTime.getMinutes();
 
    const [startHour, startMin] = startTime.split(':').map(Number);
    const [endHour, endMin] = endTime.split(':').map(Number);

    const entryTotalMinutes = entryHours * 60 + entryMinutes;
    const startTotalMinutes = startHour * 60 + startMin;
    const endTotalMinutes = endHour * 60 + endMin;

    // Handle overnight ranges (e.g., 9:30 PM to 6:30 AM)
    if (startTotalMinutes > endTotalMinutes) {
      return entryTotalMinutes >= startTotalMinutes || entryTotalMinutes <= endTotalMinutes;
    } else {
      return entryTotalMinutes >= startTotalMinutes && entryTotalMinutes <= endTotalMinutes;
    }
  };

  // Filter and sort entries
  const filteredEntries = useMemo(() => {
    let filtered = entries.filter(entry => {
      // Date filters
      if (filters.startDate) {
        const entryDate = new Date(entry.open_date);
        const filterDate = new Date(filters.startDate);
        if (entryDate < filterDate) return false;
      }

      if (filters.endDate) {
        const entryDate = new Date(entry.closed_date || entry.open_date);
        const filterDate = new Date(filters.endDate);
        filterDate.setHours(23, 59, 59, 999); // End of day
        if (entryDate > filterDate) return false;
      }

      // Game type filter
      if (filters.gameType && entry.game_name !== filters.gameType) {
        return false;
      }

      // Time range filter
      if (filters.timeRangeStart && filters.timeRangeEnd) {
        if (!isTimeInRange(entry.open_date, filters.timeRangeStart, filters.timeRangeEnd)) {
          return false;
        }
      }

      return true;
    });

    // Sort entries
    filtered.sort((a, b) => {
      let aValue = a[filters.sortBy];
      let bValue = b[filters.sortBy];

      // Handle date sorting
      if (filters.sortBy.includes('date')) {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // Handle numeric sorting
      if (filters.sortBy === 'session_mins' || filters.sortBy === 'cash_points') {
        aValue = Number(aValue) || 0;
        bValue = Number(bValue) || 0;
      }

      // Handle string sorting (for game_name, type, location)
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (filters.sortOrder === 'desc') {
        return bValue > aValue ? 1 : -1;
      } else {
        return aValue > bValue ? 1 : -1;
      }
    });

    return filtered;
  }, [entries, filters]);

  // Calculate totals for filtered entries
  const filteredTotals = useMemo(() => {
    const totalMins = filteredEntries.reduce((sum, entry) => sum + (entry.session_mins || 0), 0);
    const totalCashPoints = filteredEntries.reduce((sum, entry) => sum + (entry.cash_points || 0), 0);

    return {
      totalSessionMins: totalMins,
      totalCashPoints: totalCashPoints
    };
  }, [filteredEntries]);

  // Game type breakdown
  const gameTypeBreakdown = useMemo(() => {
    const breakdown = {};
    filteredEntries.forEach(entry => {
      const gameType = entry.game_name || 'Unknown';
      if (!breakdown[gameType]) {
        breakdown[gameType] = {
          sessions: 0,
          totalMins: 0,
          totalCashPoints: 0
        };
      }
      breakdown[gameType].sessions += 1;
      breakdown[gameType].totalMins += entry.session_mins || 0;
      breakdown[gameType].totalCashPoints += entry.cash_points || 0;
    });
    return breakdown;
  }, [filteredEntries]);

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTime = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleSort = (column) => {
    setFilters(prev => ({
      ...prev,
      sortBy: column,
      sortOrder: prev.sortBy === column && prev.sortOrder === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (column) => {
    if (filters.sortBy !== column) {
      return <ChevronUp className="h-4 w-4 text-gray-300" />;
    }
    return filters.sortOrder === 'asc'
      ? <ChevronUp className="h-4 w-4 text-gray-600" />
      : <ChevronDown className="h-4 w-4 text-gray-600" />;
  };

  const clearFilters = () => {
    setFilters({
      startDate: '',
      endDate: '',
      gameType: '',
      sortBy: 'open_date',
      sortOrder: 'desc',
      timeRangeStart: '',
      timeRangeEnd: ''
    });
  };

  // Extended Quick time range presets with 1-hour spans
  const quickTimeRanges = [
    // Standard day periods
    { label: 'Early Morning (6:00 AM - 9:00 AM)', start: '06:00', end: '09:00' },
    { label: 'Morning (9:00 AM - 12:00 PM)', start: '09:00', end: '12:00' },
    { label: 'Afternoon (12:00 PM - 3:00 PM)', start: '12:00', end: '15:00' },
    { label: 'Late Afternoon (3:00 PM - 6:00 PM)', start: '15:00', end: '18:00' },
    { label: 'Evening (6:00 PM - 9:00 PM)', start: '18:00', end: '21:00' },
    { label: 'Night (9:00 PM - 12:00 AM)', start: '21:00', end: '24:00' },
    { label: 'Late Night (12:00 AM - 3:00 AM)', start: '00:00', end: '03:00' },
    { label: 'Early Hours (3:00 AM - 6:00 AM)', start: '03:00', end: '06:00' },

    // Hourly spans starting from 6:30 AM
    { label: '6:30 AM - 7:30 AM', start: '06:30', end: '07:30' },
    { label: '7:30 AM - 8:30 AM', start: '07:30', end: '08:30' },
    { label: '8:30 AM - 9:30 AM', start: '08:30', end: '09:30' },
    { label: '9:30 AM - 10:30 AM', start: '09:30', end: '10:30' },
    { label: '10:30 AM - 11:30 AM', start: '10:30', end: '11:30' },
    { label: '11:30 AM - 12:30 PM', start: '11:30', end: '12:30' },
    { label: '12:30 PM - 1:30 PM', start: '12:30', end: '13:30' },
    { label: '1:30 PM - 2:30 PM', start: '13:30', end: '14:30' },
    { label: '2:30 PM - 3:30 PM', start: '14:30', end: '15:30' },
    { label: '3:30 PM - 4:30 PM', start: '15:30', end: '16:30' },
    { label: '4:30 PM - 5:30 PM', start: '16:30', end: '17:30' },
    { label: '5:30 PM - 6:30 PM', start: '17:30', end: '18:30' },
    { label: '6:30 PM - 7:30 PM', start: '18:30', end: '19:30' },
    { label: '7:30 PM - 8:30 PM', start: '19:30', end: '20:30' },
    { label: '8:30 PM - 9:30 PM', start: '20:30', end: '21:30' },
    { label: '9:30 PM - 10:30 PM', start: '21:30', end: '22:30' },
    { label: '10:30 PM - 11:30 PM', start: '22:30', end: '23:30' },
    { label: '11:30 PM - 12:30 AM', start: '23:30', end: '00:30' },
    { label: '12:30 AM - 1:30 AM', start: '00:30', end: '01:30' },
    { label: '1:30 AM - 2:30 AM', start: '01:30', end: '02:30' },
    { label: '2:30 AM - 3:30 AM', start: '02:30', end: '03:30' },
    { label: '3:30 AM - 4:30 AM', start: '03:30', end: '04:30' },
    { label: '4:30 AM - 5:30 AM', start: '04:30', end: '05:30' },
    { label: '5:30 AM - 6:30 AM', start: '05:30', end: '06:30' },

    // Special overnight ranges
    { label: 'Night Shift (9:30 PM - 6:30 AM)', start: '21:30', end: '06:30' },
    { label: 'Late Night Shift (11:30 PM - 6:30 AM)', start: '23:30', end: '06:30' },
    { label: 'Graveyard Shift (12:30 AM - 6:30 AM)', start: '00:30', end: '06:30' }
  ];

  const applyQuickTimeRange = (start, end) => {
    setFilters(prev => ({
      ...prev,
      timeRangeStart: start,
      timeRangeEnd: end
    }));
  };

  // Normalize phone number to +1XXXXXXXXXX format
  // This ensures all phone numbers have the +1 prefix before sending SMS
  const normalizePhoneForSMS = useCallback((phone) => {
    if (!phone || !phone.trim()) return null;
    
    const cleaned = phone.trim();
    const digits = cleaned.replace(/\D/g, '');
    
    // If already in +1XXXXXXXXXX format, return as is
    if (cleaned.startsWith('+1') && digits.length === 12) {
      return cleaned;
    }
    
    // Handle different formats
    if (digits.length === 10) {
      // 10 digits (XXXXXXXXXX): add +1 prefix
      return `+1${digits}`;
    } else if (digits.length === 11 && digits.startsWith('1')) {
      // 11 digits starting with 1 (1XXXXXXXXXX): add + prefix
      return `+${digits}`;
    } else if (cleaned.startsWith('+') && digits.length === 11) {
      // + followed by 11 digits
      if (digits.startsWith('1')) {
        return `+${digits}`;
      }
    }
    
    // If we can't normalize, return null
    return null;
  }, []);

  const handleSendMessage = async () => {
    if (!messageText.trim()) {
      toast.error('Please enter a message');
      return;
    }

    if (!player.phone) {
      toast.error('Player phone number is not available');
      return;
    }

    // Normalize phone number to ensure +1 prefix
    const normalizedPhone = normalizePhoneForSMS(player.phone);
    
    if (!normalizedPhone) {
      toast.error('Invalid phone number format. Phone number must be 10 digits (XXXXXXXXXX) or 11 digits (1XXXXXXXXXX) or in +1XXXXXXXXXX format');
      return;
    }

    setSendingMessage(true);
    try {
      // Send SMS with normalized phone number (with +1 prefix)
      const response = await playerProfileService.sendSMS(normalizedPhone, messageText, {
        player_id: player.id || null,
        player_number: player.player_number || null,
        player_name: player.player_name || null,
        send_type: 'single'
      });
      
      if (response && response.success) {
        if (isSingleSmsSkipped(response)) {
          toast(response?.data?.message || 'This number is invalid or unsubscribed. Message was not sent.');
        } else {
          toast.success('Message sent successfully!');
          setIsMessageEditable(false);
          setShowMessaging(false);
        }
      } else {
        toast.error(response?.error || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = error?.response?.data?.error || error?.response?.data?.message || error?.message || 'Failed to send message. Please try again.';
      toast.error(errorMessage);
    } finally {
      setSendingMessage(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-7xl w-full max-h-[95vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              {player.image_url ? (
                <img 
                  src={player.image_url} 
                  alt="Player" 
                  className="w-10 h-10 object-contain rounded-full"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://via.placeholder.com/40?text=NA";
                  }}
                />
              ) : (
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-blue-600">
                    {player.player_name?.charAt(0) || "N"}
                  </span>
                </div>
              )}
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  {player.player_name} ({player.player_number})
                </h2>
                <p className="text-sm text-gray-500">
                  Player History & Entries
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          <div className="p-6">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-16">
                <Loader2 className="h-12 w-12 text-blue-600 animate-spin mb-4" />
                <p className="text-gray-500 text-lg">Loading player entries...</p>
              </div>
            ) : (
              <>
                {/* Filters Section */}
                <div className="bg-gray-50 rounded-lg p-4 mb-6 space-y-4">
                  {/* First row: Game Type, Messaging Button, and Clear Filters */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <GamepadIcon className="h-5 w-5 text-gray-600" />
                        <label className="text-sm font-medium text-gray-700">Game Type:</label>
                      </div>
                      <select
                        value={filters.gameType}
                        onChange={(e) => handleFilterChange('gameType', e.target.value)}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">All Games</option>
                        {gameTypes.map(gameType => (
                          <option key={gameType} value={gameType}>{gameType}</option>
                        ))}
                      </select>
                      
                      <button
                        onClick={async () => {
                          if (showMessaging) {
                            setShowMessaging(false);
                            return;
                          }
                          setIsMessageEditable(false);
                          try {
                            const res = await playerProfileService.getSmsTemplate('default');
                            const body = res?.data?.data?.body ?? res?.data?.body;
                            if (res?.success && body != null) {
                              setMessageText(body);
                            } else {
                              setMessageText(DEFAULT_SMS_TEMPLATE);
                            }
                          } catch {
                            setMessageText(DEFAULT_SMS_TEMPLATE);
                          }
                          setShowMessaging(true);
                        }}
                        className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                          showMessaging
                            ? 'bg-blue-600 text-white hover:bg-blue-700'
                            : 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <MessageSquare size={16} />
                        Messaging
                      </button>
                    </div>

                    <button
                      onClick={clearFilters}
                      className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 underline"
                    >
                      Clear all filters
                    </button>
                  </div>

                  {/* Messaging Section */}
                  {showMessaging && (
                    <div className="bg-white border border-gray-300 rounded-lg p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <MessageSquare className="h-4 w-4 text-blue-600" />
                          <h4 className="text-sm font-medium text-gray-800">
                            Send SMS to {player.player_name}
                          </h4>
                        </div>
                        <button
                          onClick={() => {
                            setShowMessaging(false);
                            setIsMessageEditable(false);
                          }}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <X size={16} />
                        </button>
                      </div>
                      
                      {/* Phone Number Display - Prominent */}
                      <div className={`p-3 rounded-lg border-2 ${player.phone 
                        ? 'bg-blue-50 border-blue-200' 
                        : 'bg-red-50 border-red-200'
                      }`}>
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <label className="block text-xs font-semibold text-gray-700 mb-1">
                              Recipient Phone Number:
                            </label>
                            {player.phone ? (
                              <div className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-base font-bold text-blue-700">
                                    {normalizePhoneForSMS(player.phone) || player.phone}
                                  </span>
                                  {normalizePhoneForSMS(player.phone) ? (
                                    <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full font-medium">
                                      ✓ Valid (+1 added)
                                    </span>
                                  ) : (
                                    <span className="text-xs text-yellow-600 bg-yellow-100 px-2 py-1 rounded-full font-medium">
                                      ⚠ Invalid format
                                    </span>
                                  )}
                                </div>
                                {player.phone !== normalizePhoneForSMS(player.phone) && normalizePhoneForSMS(player.phone) && (
                                  <p className="text-xs text-gray-500">
                                    Original: {player.phone} → Normalized: {normalizePhoneForSMS(player.phone)}
                                  </p>
                                )}
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <span className="text-base font-semibold text-red-700">
                                  No phone number available
                                </span>
                                <span className="text-xs text-red-600 bg-red-100 px-2 py-1 rounded-full font-medium">
                                  ⚠ Cannot send SMS
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <label className="block text-xs font-medium text-gray-700">
                            Message:
                          </label>
                          {!isMessageEditable ? (
                            <button
                              onClick={() => setIsMessageEditable(true)}
                              className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                            >
                              <Edit2 size={14} />
                              Edit
                            </button>
                          ) : (
                            <button
                              onClick={async () => {
                                try {
                                  const res = await playerProfileService.saveSmsTemplate(messageText, 'default');
                                  if (res?.success) {
                                    setIsMessageEditable(false);
                                    toast.success('Message saved to database!');
                                  } else {
                                    toast.error(res?.error || 'Failed to save template');
                                  }
                                } catch (e) {
                                  toast.error(e?.message || 'Failed to save template');
                                }
                              }}
                              className="flex items-center gap-1 px-3 py-1 text-xs font-medium text-green-600 bg-green-50 rounded-md hover:bg-green-100 transition-colors"
                            >
                              <Check size={14} />
                              Save
                            </button>
                          )}
                        </div>
                        <textarea
                          value={messageText}
                          onChange={(e) => setMessageText(e.target.value)}
                          placeholder={player.phone 
                            ? `Enter your message to send to ${player.phone}...` 
                            : "Phone number is required to send SMS. Please update player profile with a valid phone number."
                          }
                          rows={12}
                          disabled={!player.phone || !isMessageEditable}
                          readOnly={!isMessageEditable}
                          className={`w-full min-h-[280px] px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y ${
                            !player.phone ? 'bg-gray-100 cursor-not-allowed' : 
                            !isMessageEditable ? 'bg-gray-50 cursor-default' : 'bg-white'
                          }`}
                        />
                        {!isMessageEditable && (
                          <p className="text-xs text-gray-500 italic">
                            Click "Edit" button to customize this message
                          </p>
                        )}
                        <div className="flex items-center justify-between">
                          <span className="text-xs text-gray-500">
                            {messageText.length} characters
                          </span>
                          <button
                            onClick={handleSendMessage}
                            disabled={sendingMessage || !messageText.trim() || !player.phone}
                            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                          >
                            {sendingMessage ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin" />
                                Sending...
                              </>
                            ) : (
                              <>
                                <Send size={16} />
                                Send Message
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}


                  {/* Fourth row: Quick Time Range Presets */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-gray-700">Time Range Filter:</span>
                      {filters.timeRangeStart && filters.timeRangeEnd && (
                        <button
                          onClick={() => {
                            setFilters(prev => ({ ...prev, timeRangeStart: '', timeRangeEnd: '' }));
                          }}
                          className="text-xs text-blue-600 hover:text-blue-800 underline"
                        >
                          Clear selection
                        </button>
                      )}
                    </div>

                    {/* Selected Time Range Display */}
                    {(filters.timeRangeStart && filters.timeRangeEnd) ? (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 text-blue-600 mr-2" />
                          <span className="text-sm font-medium text-blue-700">
                            {timeOptions.find(opt => opt.value === filters.timeRangeStart)?.display}
                            {" to "}
                            {timeOptions.find(opt => opt.value === filters.timeRangeEnd)?.display}
                          </span>
                        </div>
                        <span className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full">
                          Active Filter
                        </span>
                      </div>
                    ) : (
                      <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                        <p className="text-sm text-gray-500 mb-2">
                          No time range selected - showing all hours
                        </p>
                      </div>
                    )}

                    {/* Hourly Time Slots Section */}
                    <div
                      className="bg-white border border-gray-300 rounded-lg p-3 hover:border-blue-400 cursor-pointer transition-all"
                      onClick={() => setIsHourlyModalOpen(true)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <Filter className="h-4 w-4 text-gray-600 mr-2" />
                          <h4 className="text-sm font-medium text-gray-800">
                            Browse Hourly Time Slots
                          </h4>
                        </div>
                        <ChevronRight className="h-4 w-4 text-gray-500" />
                      </div>

                      <div className="mt-2 flex items-center text-xs text-gray-500">
                        <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded mr-2">
                          {quickTimeRanges.slice(8, -3).length} options
                        </span>
                        Click to view all hourly time slots
                      </div>
                    </div>

                    {/* Quick Presets */}
                    <div className="mt-3">
                      <h4 className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                        Popular Time Ranges:
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {quickTimeRanges.slice(0, 4).map((range, index) => (
                          <button
                            key={index}
                            onClick={() => applyQuickTimeRange(range.start, range.end)}
                            className={`px-3 py-1.5 text-xs rounded-lg transition-colors ${filters.timeRangeStart === range.start && filters.timeRangeEnd === range.end
                                ? 'bg-blue-600 text-white'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                          >
                            {range.label.split('(')[0].trim()}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-white rounded-lg border p-4 flex items-center">
                    <User className="mr-3 text-blue-500" size={20} />
                    <div>
                      <p className="text-sm text-gray-500">Filtered Entries</p>
                      <p className="text-xl font-bold">{filteredEntries.length}</p>
                      <p className="text-xs text-gray-400">of {entries.length} total</p>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg border p-4 flex items-center">
                    <Clock className="mr-3 text-green-500" size={20} />
                    <div>
                      <p className="text-sm text-gray-500">Total Session Time</p>
                      <p className="text-xl font-bold">
                        {formatTime(filteredTotals.totalSessionMins)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Game Type Breakdown */}
                {Object.keys(gameTypeBreakdown).length > 0 && (
                  <div className="bg-white rounded-lg border p-6 mb-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-4">Session Time by Game Type</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(gameTypeBreakdown).map(([gameType, stats]) => (
                        <div key={gameType} className="bg-gray-50 rounded-lg p-4">
                          <h4 className="font-medium text-gray-900 mb-2">{gameType}</h4>
                          <div className="space-y-1 text-sm text-gray-600">
                            <div className="flex justify-between">
                              <span>Sessions:</span>
                              <span className="font-medium">{stats.sessions}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Total Time:</span>
                              <span className="font-medium">{formatTime(stats.totalMins)}</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Avg Session:</span>
                              <span className="font-medium">
                                {formatTime(Math.round(stats.totalMins / stats.sessions))}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Entries Table */}
                <div className="overflow-x-auto">
                  {filteredEntries.length > 0 ? (
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th
                            className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort('open_date')}
                          >
                            <div className="flex items-center justify-between">
                              Start Date
                              {getSortIcon('open_date')}
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort('closed_date')}
                          >
                            <div className="flex items-center justify-between">
                              End Date
                              {getSortIcon('closed_date')}
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort('type')}
                          >
                            <div className="flex items-center justify-between">
                              Type
                              {getSortIcon('type')}
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort('game_name')}
                          >
                            <div className="flex items-center justify-between">
                              Game Name
                              {getSortIcon('game_name')}
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort('location')}
                          >
                            <div className="flex items-center justify-between">
                              Location
                              {getSortIcon('location')}
                            </div>
                          </th>
                          <th
                            className="px-6 py-3 text-left text-sm font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 select-none"
                            onClick={() => handleSort('session_mins')}
                          >
                            <div className="flex items-center justify-between">
                              Session Time
                              {getSortIcon('session_mins')}
                            </div>
                          </th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {filteredEntries.map((entry) => (
                          <tr key={entry.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDate(entry.open_date)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDate(entry.closed_date)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {entry.type || 'N/A'}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <span className="font-medium">{entry.game_name || 'N/A'}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {entry.location || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <span className="inline-flex items-center">
                                <Clock className="h-4 w-4 mr-1 text-gray-400" />
                                {formatTime(entry.session_mins || 0)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-16">
                      <User className="h-16 w-16 text-gray-300 mb-4" />
                      <p className="text-gray-500 text-lg">
                        {entries.length === 0
                          ? "No entries found for this player"
                          : "No entries match the current filters"
                        }
                      </p>
                      {entries.length > 0 && (
                        <button
                          onClick={clearFilters}
                          className="mt-2 text-blue-600 hover:text-blue-800 underline"
                        >
                          Clear filters to see all entries
                        </button>
                      )}
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Showing {filteredEntries.length} of {entries.length} entries
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
      <HourlyTimeSlotsModal
        isOpen={isHourlyModalOpen}
        onClose={() => setIsHourlyModalOpen(false)}
        quickTimeRanges={quickTimeRanges}
        onApplyTimeRange={applyQuickTimeRange}
      />
    </>
  );
};

export default PlayerEntriesModal;